import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { testimonials } from "@/data/testimonials";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface TestimonialsProps {
  t: (key: string) => string;
}

const AUTO_SCROLL_MS = 4000;

export function Testimonials({ t }: TestimonialsProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    skipSnaps: false,
    dragFree: false,
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi || isPaused) return;
    const id = setInterval(() => emblaApi.scrollNext(), AUTO_SCROLL_MS);
    return () => clearInterval(id);
  }, [emblaApi, isPaused]);

  return (
    <section
      className="py-24 bg-card/20"
      data-testid="section-testimonials"
      id="section-testimonials"
    >
      <div className="max-w-6xl mx-auto px-6">
        <motion.h2
          className="text-2xl md:text-3xl font-serif font-bold text-center text-foreground mb-12"
          style={{ fontFamily: "'Playfair Display', serif" }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {t("testimonials.title")}
        </motion.h2>

        <div className="relative">
          <div
            className="overflow-hidden"
            ref={emblaRef}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="flex gap-6 -ml-4">
              {testimonials.map((item, i) => (
                <div
                  key={i}
                  className="flex-[0_0_100%] md:flex-[0_0_calc(33.333%-16px)] min-w-0 pl-4"
                >
                  <div className="bg-card/80 backdrop-blur-sm border border-border/40 rounded-2xl p-8 h-full hover:border-primary/20 transition-all duration-300 relative">
                    <span
                      className="absolute -top-2 -left-2 text-6xl font-serif text-primary/20 select-none"
                      aria-hidden
                    >
                      "
                    </span>
                    <div className="flex gap-1 mb-4">
                      {Array.from({ length: item.rating }).map((_, j) => (
                        <Star
                          key={j}
                          className="w-4 h-4 fill-primary text-primary"
                          aria-hidden
                        />
                      ))}
                    </div>
                    <p className="font-serif italic text-foreground/90 text-lg leading-relaxed relative z-10">
                      {item.quote}
                    </p>
                    <div className="mt-6 flex items-center gap-2">
                      <span className="font-medium text-foreground">{item.name}</span>
                      <span className="text-muted-foreground" aria-hidden>
                        {item.flag}
                      </span>
                      <span className="text-sm text-muted-foreground">{item.country}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="icon"
              onClick={scrollPrev}
              disabled={!canPrev}
              className="rounded-full border-primary/30 text-primary hover:bg-primary/10"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => emblaApi?.scrollTo(i)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all",
                    i === selectedIndex ? "bg-primary scale-125" : "bg-primary/30"
                  )}
                  aria-label={t("testimonials.goTo") + " " + (i + 1)}
                />
              ))}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={scrollNext}
              disabled={!canNext}
              className="rounded-full border-primary/30 text-primary hover:bg-primary/10"
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
